import React, { useState } from "react";
import { Leaf, ShieldCheck, CheckCircle2, ArrowRight, HelpCircle } from "lucide-react";
import { Helmet } from "react-helmet";
import app from "./firebase";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";

const db = getFirestore(app);

// Data configuration for search intent pages
const SEARCH_PAGES_DATA = {
  "buy-bulk-cocoa-nigeria": {
    title: "Buy Bulk Cocoa Beans in Nigeria — Verified Export Grade Suppliers",
    h1: "Buy Bulk Cocoa Beans in Nigeria",
    subtitle: "Source Grade A fermented main crop cocoa beans directly from verified West African origin suppliers with escrow payment security.",
    metaDescription: "Looking to buy bulk cocoa beans in Nigeria? Agrivestia connects international buyers & local processors with verified cocoa suppliers, quality testing, and escrow.",
    keywords: "buy cocoa Nigeria, wholesale cocoa beans Lagos, export cocoa supplier Ondo, bulk cocoa price per ton",
    crop: "Cocoa Beans",
    specs: [
      { name: "Grade", val: "Grade A Main Crop" },
      { name: "Moisture Content", val: "< 7.5%" },
      { name: "Slaty / Mold Beans", val: "< 3% max" },
      { name: "Packaging", val: "65kg Export Jute Bags" },
      { name: "Origin States", val: "Ondo, Osun, Cross River, Ogun" }
    ],
    faqs: [
      { q: "What is the minimum order volume for bulk cocoa?", a: "Minimum order volume starts at 20 Metric Tons (one full container load) for export contracts, or 5 MT for domestic processors." },
      { q: "How is cocoa quality verified prior to payment release?", a: "Every batch is inspected at warehouse by certified quality assessors. Escrow funds are only released after moisture and bean count verification." }
    ]
  },
  "buy-cashew-nigeria": {
    title: "Buy Raw & Processed Cashew Nuts in Nigeria — KOR 48+ Suppliers",
    h1: "Buy Bulk Raw Cashew Nuts in Nigeria",
    subtitle: "Source sun-dried raw cashew nuts (KOR 48-52 lbs) and processed kernels from verified cashew suppliers across Nigeria.",
    metaDescription: "Buy raw cashew nuts in Nigeria with guaranteed KOR outturn ratio, low moisture, and secure escrow trade. Connect with vetted cashew suppliers today.",
    keywords: "buy cashew nuts Nigeria, raw cashew nuts exporter Ogbomoso, KOR 48 cashew price, wholesale cashew nuts",
    crop: "Raw Cashew Nuts",
    specs: [
      { name: "Outturn Ratio (KOR)", val: "KOR 48 - 52 lbs" },
      { name: "Nut Count", val: "180 - 200 nuts/kg" },
      { name: "Moisture Content", val: "< 8% Sun-dried" },
      { name: "Foreign Matter", val: "< 0.5%" },
      { name: "Origin States", val: "Oyo (Ogbomoso), Kogi, Kwara, Enugu" }
    ],
    faqs: [
      { q: "What is the KOR standard guaranteed by Agrivestia?", a: "We match buyers with verified cashew lots offering KOR 48+ guaranteed by independent lab test before vessel loading or warehouse release." }
    ]
  },
  "buy-sesame-seeds-nigeria": {
    title: "Buy Export Grade White Sesame Seeds in Nigeria — 99.5% Purity",
    h1: "Buy Wholesale White Sesame Seeds in Nigeria",
    subtitle: "Source machine-cleaned white and natural sesame seeds with 99.5% purity and >51% oil content for export.",
    metaDescription: "Buy white sesame seeds from Nigeria. Machine cleaned, 99.5% purity, low moisture. Verified suppliers, escrow protection, FOB/CIF sourcing.",
    keywords: "buy sesame seeds Nigeria, white sesame exporter Kano, sesame seeds price per ton, Benue sesame supplier",
    crop: "White Sesame Seeds",
    specs: [
      { name: "Purity Level", val: "99.5% Machine Cleaned" },
      { name: "Oil Content", val: "> 51%" },
      { name: "Moisture Content", val: "< 6%" },
      { name: "Admixture", val: "< 0.5%" },
      { name: "Origin States", val: "Jigawa, Kano, Benue, Nasarawa" }
    ],
    faqs: [
      { q: "Can sesame seeds be shipped under CIF export terms?", a: "Yes, our logistics desk handles FOB Port of Lagos/Tin Can and CIF destination port deliveries." }
    ]
  },
  "verified-agricultural-suppliers-nigeria": {
    title: "Verified Agricultural Suppliers Directory Nigeria — Escrow Vetted",
    h1: "Find Verified Agricultural Suppliers in Nigeria",
    subtitle: "Browse vetted cocoa, cashew, sesame, and grain suppliers in Nigeria tested for CAC registration, warehouse capacity, and trade reliability.",
    metaDescription: "Access Nigeria's directory of verified agricultural suppliers. Prevent fraud with bank escrow, physical warehouse audits, and quality inspection.",
    keywords: "verified agricultural suppliers Nigeria, trusted farm produce suppliers, commodity suppliers directory Africa",
    crop: "Agricultural Commodities",
    specs: [
      { name: "Supplier Vetting", val: "Physical Warehouse Audit + CAC Check" },
      { name: "Trade Security", val: "Agrivestia Bank Escrow Guarantee" },
      { name: "Supported Crops", val: "Cocoa, Cashew, Sesame, Ginger, Rice, Maize" }
    ],
    faqs: [
      { q: "How does Agrivestia verify agricultural suppliers?", a: "Our field team conducts physical warehouse inspections, reviews past delivery records, and verifies legal registration before approving suppliers." }
    ]
  },
  "sell-crops-to-bulk-buyers": {
    title: "Sell Crops to Bulk Buyers in Nigeria — Direct Processor & Exporter Demand",
    h1: "Sell Your Crop Harvest directly to Bulk Buyers",
    subtitle: "Connect your farm produce or warehouse inventory directly with verified corporate buyers, local processors, and export merchants.",
    metaDescription: "Sell crops online in Nigeria. Submit your available volume for cocoa, cashew, sesame, or corn and get matched with verified corporate buyers.",
    keywords: "sell crops online Nigeria, bulk crop buyers, farmer crop selling portal, sell harvest to processors",
    crop: "Farm Produce / Crops",
    specs: [
      { name: "Target Buyers", val: "Industrial Processors, Exporters, Wholesalers" },
      { name: "Payment Method", val: "Guaranteed Escrow Payout upon Delivery" },
      { name: "Upfront Fee", val: "₦0 (Free Supplier Demand Check)" }
    ],
    faqs: [
      { q: "How quickly do buyers respond to posted crop offers?", a: "Matching buyers are notified immediately upon submission. Most suppliers receive buyer quotes within 24 hours." }
    ]
  },
  "farm-investment-opportunities-nigeria": {
    title: "Farm Investment Opportunities in Nigeria — Transparent Risk & Briefings",
    h1: "Farm Investment Opportunities & Project Briefings",
    subtitle: "Review transparent agricultural trade finance and farm aggregation opportunities with clear risk disclosures and milestone-based escrow.",
    metaDescription: "Explore verified farm investment opportunities in Nigeria. Review transparent terms, risk disclosures, and satellite monitored operations.",
    keywords: "farm investment opportunities Nigeria, invest in agriculture Nigeria, agri trade financing, transparent farm returns",
    crop: "Agri-Capital Opportunities",
    specs: [
      { name: "Structure", val: "Trade Finance & Milestone Aggregation" },
      { name: "Risk Governance", val: "Satellite Field Tracking + Insured Storage" },
      { name: "Investment Horizon", val: "6 to 24 Months" }
    ],
    faqs: [
      { q: "What risk disclosures apply to farm investments?", a: "Agricultural investments carry market, weather, and operational risks. Agrivestia provides escrow allocation control but does not offer guaranteed returns." }
    ]
  },
  "agricultural-commodity-prices-nigeria": {
    title: "Agricultural Commodity Prices in Nigeria — Live Market Trends & Outlook",
    h1: "Agricultural Commodity Market Prices & Sourcing",
    subtitle: "Track live market price benchmarks for cocoa, cashew nuts, white sesame seeds, ginger, and corn in West Africa.",
    metaDescription: "Check current agricultural commodity prices in Nigeria. Real-time market trends for cocoa, cashew, sesame seeds, and grains.",
    keywords: "agricultural commodity prices Nigeria, cocoa price per ton Nigeria, cashew nut market price, sesame seed market rate",
    crop: "Commodity Price Benchmarks",
    specs: [
      { name: "Cocoa Beans", val: "Market Benchmark (Main Crop Grade A)" },
      { name: "Raw Cashew Nuts", val: "Market Benchmark (KOR 48+)" },
      { name: "White Sesame", val: "Market Benchmark (99.5% Cleaned)" }
    ],
    faqs: [
      { q: "How often are market commodity prices updated?", a: "Prices are updated weekly based on physical transactions recorded across major primary trading hubs in Nigeria." }
    ]
  },
  "export-agricultural-produce-from-nigeria": {
    title: "Export Agricultural Produce from Nigeria — Quality Control & Escrow Logistics",
    h1: "Export Agricultural Produce from Nigeria Seamlessly",
    subtitle: "End-to-end export sourcing, quality testing (SGS/Bureau Veritas standards), port clearance support, and escrow financing.",
    metaDescription: "Export agricultural commodities from Nigeria safely. Agrivestia handles supplier vetting, export quality testing, port logistics, and escrow.",
    keywords: "export agricultural produce Nigeria, agribusiness export compliance, cocoa export process, cashew export port logistics",
    crop: "Export Commodities",
    specs: [
      { name: "Compliance", val: "Phytosanitary Certificate, NEPC Export License" },
      { name: "Testing Standards", val: "SGS / Bureau Veritas Compatible Quality" },
      { name: "Port Logistics", val: "Lagos Port / Tin Can Island Terminal Clearance" }
    ],
    faqs: [
      { q: "Can Agrivestia assist international buyers with export documentation?", a: "Yes, our export desk coordinates phytosanitary certification, bill of lading, and custom clearing documentation." }
    ]
  }
};

export default function SearchLandingPage({ pageKey }) {
  const data = SEARCH_PAGES_DATA[pageKey] || SEARCH_PAGES_DATA["buy-bulk-cocoa-nigeria"];

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    details: "",
    intent: "Buy / Source"
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, "search_leads"), {
        ...formData,
        pageKey,
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.warn("Firestore save fallback:", err);
    }
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div style={{ fontFamily: "var(--font-sans, system-ui, sans-serif)", color: "#1e293b", background: "#f8fafc", minHeight: "100vh" }}>
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.metaDescription} />
        <meta name="keywords" content={data.keywords} />
      </Helmet>

      {/* Header */}
      <header style={{ background: "#ffffff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={20} color="white" />
            </div>
            <span style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>AGRIVESTIA <span style={{ color: "#16a34a", fontSize: "14px", fontWeight: 700 }}>TRADE</span></span>
          </a>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <a href="/central_market" style={{ color: "#475569", textDecoration: "none", fontSize: "14px", fontWeight: 600 }}>Central Market</a>
            <a href="#quote-form" style={{ background: "#16a34a", color: "#ffffff", padding: "10px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px" }}>Request Information</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={{ background: "linear-gradient(180deg, #052e16 0%, #0f172a 100%)", color: "#ffffff", padding: "60px 24px 80px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "48px", alignItems: "center" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 700, color: "#4ade80", marginBottom: "20px" }}>
              <ShieldCheck size={16} /> Verified Commodity & Market Portal
            </div>
            <h1 style={{ fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 900, lineHeight: 1.15, marginBottom: "20px", letterSpacing: "-0.02em" }}>
              {data.h1}
            </h1>
            <p style={{ fontSize: "18px", color: "#94a3b8", lineHeight: 1.6, marginBottom: "32px" }}>
              {data.subtitle}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", fontSize: "14px", color: "#cbd5e1" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Pre-Inspected Commodity Quality</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Bank Escrow Payment Protection</div>
            </div>
          </div>

          {/* Form */}
          <div id="quote-form" style={{ background: "#ffffff", borderRadius: "16px", padding: "32px", color: "#0f172a", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3)" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "#dcfce7", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <CheckCircle2 size={36} />
                </div>
                <h3 style={{ fontSize: "24px", fontWeight: 800, marginBottom: "12px" }}>Inquiry Submitted!</h3>
                <p style={{ color: "#475569", fontSize: "15px", lineHeight: 1.6, marginBottom: "24px" }}>
                  Thank you, <strong>{formData.name}</strong>. Our trade desk has received your request regarding <strong>{data.crop}</strong>. We will contact you on WhatsApp/phone within 1 business day.
                </p>
                <a href="/central_market" style={{ background: "#16a34a", color: "#ffffff", padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, display: "inline-block" }}>
                  Browse Central Market Tenders &rarr;
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "4px" }}>Connect with Agrivestia Desk</h2>
                <p style={{ fontSize: "13px", color: "#64748b", margin: "-8px 0 8px" }}>Get verified supplier pricing, quality specs, or market details.</p>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Primary Intent</label>
                  <select name="intent" value={formData.intent} onChange={(e) => setFormData({...formData, intent: e.target.value})} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                    <option>Buy / Source Commodity</option>
                    <option>Sell Commodity Harvest</option>
                    <option>Invest / Trade Finance</option>
                    <option>General Sourcing Inquiry</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Your Full Name *</label>
                  <input type="text" required placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>WhatsApp / Phone *</label>
                    <input type="tel" required placeholder="+234 800 000 0000" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Email Address *</label>
                    <input type="email" required placeholder="name@company.com" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Volume & Delivery Details</label>
                  <textarea rows="3" placeholder="Specify estimated volume, target delivery state/port, or custom requirements..." value={formData.details} onChange={(e) => setFormData({...formData, details: e.target.value})} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }}></textarea>
                </div>

                <button type="submit" disabled={loading} style={{ background: "#16a34a", color: "#ffffff", border: "none", padding: "14px", borderRadius: "8px", fontWeight: 800, fontSize: "16px", cursor: "pointer", marginTop: "8px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  {loading ? "Submitting Inquiry..." : "Submit Inquiry Now"} <ArrowRight size={18} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Specifications / Highlights */}
      <section style={{ padding: "60px 24px", maxWidth: "1200px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "28px", fontWeight: 900, color: "#0f172a", marginBottom: "24px" }}>Specifications & Key Sourcing Parameters</h2>
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px", marginBottom: "48px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
            {data.specs.map((item, idx) => (
              <div key={idx} style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "4px" }}>{item.name}</div>
                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>{item.val}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <h2 style={{ fontSize: "24px", fontWeight: 900, color: "#0f172a", marginBottom: "20px" }}>Frequently Asked Questions</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {data.faqs.map((faq, idx) => (
            <div key={idx} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                <HelpCircle size={18} color="#16a34a" /> {faq.q}
              </h3>
              <p style={{ color: "#475569", fontSize: "14px", lineHeight: 1.6, margin: 0, paddingLeft: "26px" }}>{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "#052e16", color: "#94a3b8", padding: "40px 24px", textAlign: "center", fontSize: "14px" }}>
        <p>&copy; 2026 Agrivestia Trade Network. All rights reserved. | <a href="/" style={{ color: "#4ade80", textDecoration: "none" }}>Homepage</a> | <a href="/central_market" style={{ color: "#4ade80", textDecoration: "none" }}>Central Market</a></p>
      </footer>
    </div>
  );
}
